import {useMemo, useState} from 'react'
import {useForm} from '../../../common/hooks/useForm'
import {BoxColor} from '../../../common/types/box'
import {resolveCircuitBoardPort} from '../../../common/utils/resolveCircuitBoardPort'
import {resolveCoordinates} from '../../../common/utils/resolveCoordinates'
import {
  AllOrganisationsDocument,
  GQL_AddBoxResponseType,
  GQL_ContractType,
  Maybe,
  useAddBoxMutation,
  useClearBoxSerialNumberMutation,
  useEditBoxMutation,
  useEditOrganisationMutation,
  useSetBoxSerialNumberMutation,
} from '../../../generated/graphql'
import {transformBoxColorToGQL} from '../../../gql/transformBox'
import {LocationForm, LocationTranslationsForm} from '../../location/add/AddLocation'
import {buildLocationTranslationsInput} from '../../location/add/buildLocationTranslationsInput'
import {NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES} from '../../location/translationLanguages'
import {resolveSerialErrorMessage} from '../serial/resolveSerialErrorMessage'

type Args = {
  compartmentForms: Array<any>
  resetAllCompartments: () => void
  organisations: Array<{
    id: string
    name: string
    boxes: Array<{id: string}>
  }>
  boxesForSelect: Array<{id: string; name: string}>
}

export type AddBoxFormState = {
  circuitBoardId: string
  color: BoxColor
  contractType: '' | GQL_ContractType
  name: string
  numberOfCompartments: number

  // NOTE: visual default should show "Free with penalty"
  priceWeight: string

  sponsorId: string
  organisationId: string

  // Optional serial number captured up front via SerialNumberPrompt (empty = assign later).
  serialNumber: string
  // Set when the serial is currently on another box and the user opted to move it here.
  serialTransferFromBoxId: string
  serialTransferFromBoxName: string
}

type CompartmentItem = {label: string; value: number}

const numberOfCompartmentOptions: CompartmentItem[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map((number) => ({
  label: String(number),
  value: number,
}))

const initialAddBoxForm: AddBoxFormState = {
  circuitBoardId: '',
  color: '#B40A7A',
  contractType: '',
  numberOfCompartments: 14,
  name: '',

  // Default selection requested:
  // This is the id used across the system for “Free with penalty”.
  priceWeight: 'free_price_weight_id',

  sponsorId: '',
  organisationId: '',

  serialNumber: '',
  serialTransferFromBoxId: '',
  serialTransferFromBoxName: '',
}

const initialLocationTranslationsForm: LocationTranslationsForm = NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES.reduce(
  (acc, lang) => ({...acc, [lang]: {description: ''}}),
  {} as LocationTranslationsForm,
)

const initialLocationForm: LocationForm = {
  name: '',
  description: '',
  locality: '',
  lat: '',
  long: '',
  translations: initialLocationTranslationsForm,
}

const resolveErrorMessage = (type: Maybe<GQL_AddBoxResponseType> | undefined): string => {
  switch (type) {
    case GQL_AddBoxResponseType.NoPermissionToAddBox:
      return 'No permission to add box'
  }
  return 'Could not add box'
}

export const useAddBoxForm = ({compartmentForms, resetAllCompartments, organisations}: Args) => {
  const {form, reset, updateField} = useForm<AddBoxFormState>(initialAddBoxForm)
  const locationForm = useForm<LocationForm>(useMemo(() => initialLocationForm, []))

  const [addBox, {loading}] = useAddBoxMutation()
  const [editBox] = useEditBoxMutation()
  const [editOrganisation] = useEditOrganisationMutation()
  const [setBoxSerialNumber] = useSetBoxSerialNumberMutation()
  const [clearBoxSerialNumber] = useClearBoxSerialNumberMutation()

  const [success, setSuccess] = useState<null | {
    boxId: string
    boxLabel: string
    boxUrl: string
    // Set when the box was created but the serial number could not be applied. The box still
    // exists; the admin can finish via the serial-number admin page.
    serialWarning?: string
    // Set when a serial number was successfully moved here from another box, so the success
    // screen can confirm the move and link back to the now serial-less old box.
    serialMove?: {fromBoxId: string; fromBoxName: string; serial: string}
  }>(null)

  const onCreateAnother = (e?: any) => {
    if (e?.preventDefault) e.preventDefault()

    setSuccess(null)
    reset()
    resetAllCompartments()
    locationForm.reset()
  }

  const onSave = async () => {
    const result = await addBox({
      variables: {
        boxInput: {
          compartments: compartmentForms.slice(0, form.numberOfCompartments).map((compForm: any) => ({
            circuitBoardPort: resolveCircuitBoardPort(compForm.form.circuitBoardPort),
            locked: compForm.form.locked,
            name: compForm.form.name,
            productId: compForm.form.productId,
          })),
          circuitBoardId: form.circuitBoardId,
          color: transformBoxColorToGQL(form.color),
          name: form.name,
          location: {
            description: locationForm.form.description,
            name: locationForm.form.name,
            locality: locationForm.form.locality,
            coordinates: resolveCoordinates(locationForm.form.lat, locationForm.form.long),
            translations: buildLocationTranslationsInput(
              locationForm.form.description,
              locationForm.form.translations ?? initialLocationTranslationsForm,
            ),
          },
          priceWeight: form.priceWeight || undefined,
          sponsorId: form.sponsorId,
        },
      },

      refetchQueries: ['allBoxesForSelect'],
    })

    const type = result?.data?.addBox?.type ?? null

    if (type !== 'Success') {
      throw new Error(resolveErrorMessage(type as any))
    }

    const newBoxId = result?.data?.addBox?.box?.id ?? null
    if (!newBoxId) {
      throw new Error('AddBox succeeded but did not return a box id.')
    }

    if (form.contractType) {
      await editBox({
        variables: {
          boxId: newBoxId,
          boxInput: {contractType: form.contractType},
        },
      })
    }

    if (form.organisationId) {
      const org = organisations.find((o) => o.id === form.organisationId)
      if (org) {
        const existing = (org.boxes ?? []).map((b) => b.id)
        const merged = Array.from(new Set([...existing, newBoxId]))

        await editOrganisation({
          variables: {
            organisationId: org.id,
            editOrganisation: {boxIds: merged},
          },
          refetchQueries: [{query: AllOrganisationsDocument}],
        })
      }
    }

    // Serial number is applied here as a post-save step rather than as part of addBox.
    // Rationale: the backend addBox mutation/service does not accept a serial number, and a
    // separate setBoxSerialNumber mutation already owns format/checksum validation and
    // uniqueness enforcement. Reusing it keeps this an admin-only frontend change with no
    // backend/GraphQL-schema change — the same post-save pattern used above for contract
    // type and organisation.
    //
    // Trade-off: the box is already created by the time we set the serial, so if the serial
    // step fails (e.g. invalid, or a collision we couldn't detect up front) the box exists
    // without a serial. We surface a clear warning on the success screen; the admin can then
    // assign/move the serial via the serial-number admin page.
    //
    // Future improvement: add an optional serialNumber to AddBoxInput and the server addBox
    // service so the box and serial are created atomically — and, longer term, move serials
    // to a dedicated PhysicalBox/HardwareBox table.
    let serialWarning: string | undefined
    let serialMove: {fromBoxId: string; fromBoxName: string; serial: string} | undefined
    if (form.serialNumber) {
      const serial = form.serialNumber.trim()
      const setSerialOn = (boxId: string) => setBoxSerialNumber({variables: {boxId, serialNumber: serial}, refetchQueries: ['boxesForSerialAdmin']})

      if (form.serialTransferFromBoxId) {
        // Moving the serial off an existing box (reusing a physical box from another org). The
        // unique constraint forces clear-then-set, which leaves a brief window where the serial
        // lives on neither box. If the set fails we restore it to the original box so the
        // physical box's serial is never silently lost.
        const fromBoxId = form.serialTransferFromBoxId

        const clearResult = await clearBoxSerialNumber({variables: {boxId: fromBoxId}, refetchQueries: ['boxesForSerialAdmin']})
        const clearType = clearResult.data?.clearBoxSerialNumber?.type ?? null

        if (clearType !== 'Success') {
          // Old box untouched, so nothing was lost — just report why the move couldn't start.
          serialWarning = `Box created, but the serial number could not be moved from the original box: ${resolveSerialErrorMessage(clearType)}`
        } else {
          const setResult = await setSerialOn(newBoxId)
          const setType = setResult.data?.setBoxSerialNumber?.type ?? null

          if (setType !== 'Success') {
            // Roll back so the serial isn't orphaned.
            const restore = await setSerialOn(fromBoxId)
            const restored = restore.data?.setBoxSerialNumber?.type === 'Success'
            serialWarning = restored
              ? `Box created, but the serial number could not be moved here (${resolveSerialErrorMessage(setType)}). It was left on the original box.`
              : 'Box created, but moving the serial number failed and it could not be restored to the original box. Resolve this on the serial-number admin page.'
          } else {
            serialMove = {fromBoxId, fromBoxName: form.serialTransferFromBoxName, serial}
          }
        }
      } else {
        const setResult = await setSerialOn(newBoxId)
        const setType = setResult.data?.setBoxSerialNumber?.type ?? null
        if (setType !== 'Success') {
          serialWarning = `Box created, but the serial number could not be set: ${resolveSerialErrorMessage(setType)}`
        }
      }
    }

    setSuccess({
      boxId: newBoxId,
      boxLabel: `Box ${form.name}`,
      boxUrl: `/box/${newBoxId}`,
      serialWarning,
      serialMove,
    })
  }

  return {
    form,
    updateField,
    locationForm,
    loading,
    success,
    onSave,
    onCreateAnother,
    numberOfCompartmentOptions,
  }
}
